import { motion } from "motion/react";
import {
  MapPin,
  Clock,
  Phone,
  Mail,
  CreditCard,
  ShieldCheck,
  Heart,
  ParkingCircle,
  Users,
  Home,
  ArrowRight,
} from "lucide-react";
import { siteData } from "../data/mock";

const amenityIcons = {
  "LGBTQ+ Friendly": Users,
  "Safe Neighborhood": ShieldCheck,
  "Private Treatment Rooms": Home,
  "Easy Parking": ParkingCircle,
  "Credit Cards Accepted": CreditCard,
  "Comfortable Environment": Heart,
};

export const ContactSection = () => {
  const { clinic, patientInfo } = siteData;
  const address = clinic.address;
  const fullAddress = `${address.street}, ${address.city}, ${address.state} ${address.zip}`;
  const mapQuery = encodeURIComponent(fullAddress);

  return (
    <section
      id="contact"
      className="py-20 md:py-28"
      style={{ background: "rgba(255,244,232,0.35)" }}
      aria-labelledby="contact-heading"
    >
      <div className="max-w-[1280px] mx-auto px-5 md:px-9">
        {/* Section header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="mb-14"
        >
          <span className="font-mono text-[11px] uppercase tracking-widest text-[#999999] block mb-3">
            Get in Touch
          </span>
          <h2
            id="contact-heading"
            style={{
              fontFamily: "'Cormorant Garamond', serif",
              fontSize: "clamp(1.75rem, 4vw, 2.5rem)",
              lineHeight: 1.15,
              fontWeight: 600,
              color: "#232323",
              letterSpacing: "-0.01em",
            }}
          >
            Visit Our Clinic
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
          {/* Map */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="lg:col-span-3 rounded-2xl overflow-hidden"
            style={{
              border: "1px solid rgba(0,0,0,0.06)",
              minHeight: "380px",
            }}
          >
            <iframe
              title="Young Vitality Acupuncture clinic location on Google Maps - 7041 Owensmouth Ave, Suite 106, Los Angeles, CA 91303"
              src={`https://maps.google.com/maps?q=${mapQuery}&t=&z=15&ie=UTF8&iwloc=&output=embed`}
              width="100%"
              height="100%"
              style={{ border: 0, minHeight: "380px" }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              aria-label="Interactive Google Map showing clinic location"
            />
          </motion.div>

          {/* Contact details */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="lg:col-span-2 flex flex-col gap-5"
          >
            {/* Address card */}
            <div
              className="bg-white rounded-xl p-6"
              role="group"
              aria-label="Clinic address and phone number"
              style={{
                border: "1px solid rgba(0,0,0,0.04)",
                boxShadow: "0 2px 8px rgba(0,0,0,0.03)",
              }}
            >
              <div className="flex items-start gap-3 mb-4">
                <div
                  className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0"
                  style={{ background: "#FEEFDC" }}
                  aria-hidden="true"
                >
                  <MapPin size={16} strokeWidth={1.5} color="#BCA182" />
                </div>
                <address style={{ fontStyle: "normal" }}>
                  <p
                    style={{
                      fontFamily: "'Inter', sans-serif",
                      fontSize: "0.9rem",
                      fontWeight: 600,
                      color: "#232323",
                      margin: "0 0 2px 0",
                    }}
                  >
                    {address.street}
                  </p>
                  <p
                    style={{
                      fontFamily: "'Inter', sans-serif",
                      fontSize: "0.85rem",
                      color: "#353535",
                      margin: "0 0 2px 0",
                    }}
                  >
                    {address.city}, {address.state} {address.zip}
                  </p>
                  <p
                    className="font-mono text-[10px] uppercase tracking-widest text-[#999999] mt-1"
                    style={{ margin: 0 }}
                  >
                    {address.area}
                  </p>
                </address>
              </div>

              <a
                href={clinic.phoneLink}
                className="flex items-center gap-3 no-underline group py-3 border-t"
                style={{ borderColor: "rgba(0,0,0,0.06)" }}
                aria-label={`Call Young Vitality Acupuncture at ${clinic.phone}`}
              >
                <div
                  className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0"
                  style={{ background: "#E4EDF8" }}
                  aria-hidden="true"
                >
                  <Phone size={16} strokeWidth={1.5} color="#768597" />
                </div>
                <div>
                  <p
                    className="font-mono text-[10px] uppercase tracking-widest text-[#999999]"
                    style={{ margin: "0 0 2px 0" }}
                  >
                    Call us
                  </p>
                  <p
                    className="group-hover:text-[#232323] transition-colors duration-200"
                    style={{
                      fontFamily: "'Inter', sans-serif",
                      fontSize: "0.95rem",
                      fontWeight: 600,
                      color: "#353535",
                      margin: 0,
                    }}
                  >
                    {clinic.phone}
                  </p>
                </div>
              </a>

              <a
                href={`mailto:${clinic.email}`}
                className="flex items-center gap-3 no-underline group py-3 border-t"
                style={{ borderColor: "rgba(0,0,0,0.06)" }}
                aria-label={`Email Young Vitality Acupuncture at ${clinic.email}`}
              >
                <div
                  className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0"
                  style={{ background: "#FEEFDC" }}
                  aria-hidden="true"
                >
                  <Mail size={16} strokeWidth={1.5} color="#BCA182" />
                </div>
                <div>
                  <p
                    className="font-mono text-[10px] uppercase tracking-widest text-[#999999]"
                    style={{ margin: "0 0 2px 0" }}
                  >
                    Email us
                  </p>
                  <p
                    className="group-hover:text-[#232323] transition-colors duration-200"
                    style={{
                      fontFamily: "'Inter', sans-serif",
                      fontSize: "0.85rem",
                      fontWeight: 600,
                      color: "#353535",
                      margin: 0,
                    }}
                  >
                    {clinic.email}
                  </p>
                </div>
              </a>

            {/* Hours card */}
            </div>
            <div
              className="bg-white rounded-xl p-6 flex-1"
              role="group"
              aria-label="Office hours"
              style={{
                border: "1px solid rgba(0,0,0,0.04)",
                boxShadow: "0 2px 8px rgba(0,0,0,0.03)",
              }}
            >
              <div className="flex items-center gap-2.5 mb-4">
                <div
                  className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0"
                  style={{ background: "#b8d1ba" }}
                  aria-hidden="true"
                >
                  <Clock size={16} strokeWidth={1.5} color="#5E8C61" />
                </div>
                <p
                  style={{
                    fontFamily: "'Inter', sans-serif",
                    fontSize: "0.9rem",
                    fontWeight: 600,
                    color: "#232323",
                    margin: 0,
                  }}
                >
                  Office Hours
                </p>
              </div>
              <dl className="space-y-2">
                {clinic.hours.map((h) => (
                  <div
                    key={h.day}
                    className="flex justify-between items-center"
                  >
                    <dt
                      style={{
                        fontFamily: "'Inter', sans-serif",
                        fontSize: "0.82rem",
                        color: h.hours === "Closed" ? "#999999" : "#353535",
                      }}
                    >
                      {h.day}
                    </dt>
                    <dd
                      className="font-mono text-[11px]"
                      style={{
                        color: h.hours === "Closed" ? "#999999" : "#232323",
                        fontWeight: h.hours === "Closed" ? 400 : 500,
                        margin: 0,
                      }}
                    >
                      {h.hours}
                    </dd>
                  </div>
                ))}
              </dl>
            </div>
          </motion.div>
        </div>

        {/* Insurance & Amenities */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-8">
          {/* Insurance */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.4 }}
            className="bg-white rounded-xl p-6"
            role="group"
            aria-label="Insurance and payment information"
            style={{
              border: "1px solid rgba(0,0,0,0.04)",
              boxShadow: "0 2px 8px rgba(0,0,0,0.03)",
            }}
          >
            <div className="flex items-center gap-2.5 mb-4">
              <div
                className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0"
                style={{ background: "#F9E8FA" }}
                aria-hidden="true"
              >
                <ShieldCheck size={16} strokeWidth={1.5} color="#987D9C" />
              </div>
              <p
                style={{
                  fontFamily: "'Inter', sans-serif",
                  fontSize: "0.9rem",
                  fontWeight: 600,
                  color: "#232323",
                  margin: 0,
                }}
              >
                Insurance & Payment
              </p>
            </div>
            <ul className="space-y-2.5 list-none p-0 m-0">
              {patientInfo.insurance.map((item) => (
                <li key={item} className="flex items-center gap-2.5">
                  <div
                    className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                    style={{ background: "#BCA182" }}
                    aria-hidden="true"
                  />
                  <span
                    style={{
                      fontFamily: "'Inter', sans-serif",
                      fontSize: "0.85rem",
                      color: "#353535",
                    }}
                  >
                    {item}
                  </span>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Amenities */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.4, delay: 0.1 }}
            className="bg-white rounded-xl p-6"
            role="group"
            aria-label="Clinic amenities"
            style={{
              border: "1px solid rgba(0,0,0,0.04)",
              boxShadow: "0 2px 8px rgba(0,0,0,0.03)",
            }}
          >
            <p
              className="mb-4"
              style={{
                fontFamily: "'Inter', sans-serif",
                fontSize: "0.9rem",
                fontWeight: 600,
                color: "#232323",
                margin: "0 0 16px 0",
              }}
            >
              Amenities
            </p>
            <ul className="grid grid-cols-2 gap-3 list-none p-0 m-0">
              {patientInfo.amenities.map((item) => {
                const Icon = amenityIcons[item] || Heart;
                return (
                  <li key={item} className="flex items-center gap-2.5">
                    <Icon size={14} strokeWidth={1.5} color="#999999" aria-hidden="true" />
                    <span
                      style={{
                        fontFamily: "'Inter', sans-serif",
                        fontSize: "0.82rem",
                        color: "#353535",
                      }}
                    >
                      {item}
                    </span>
                  </li>
                );
              })}
            </ul>
          </motion.div>
        </div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center mt-14"
        >
          <a
            href={clinic.phoneLink}
            className="inline-flex items-center gap-2 bg-[#232323] text-white no-underline rounded-full px-8 py-4 font-mono text-[13px] font-medium uppercase tracking-wide hover:bg-[#353535] hover:scale-[1.02] active:scale-[0.98] transition-transform duration-200"
            aria-label={`Book your appointment today - call ${clinic.phone}`}
          >
            Book Your Appointment Today
            <ArrowRight size={15} strokeWidth={2} aria-hidden="true" />
          </a>
        </motion.div>
      </div>
    </section>
  );
};
